import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from '../lib/pushNotifications';
import {
  Bell,
  User,
  AlertCircle,
  CheckCircle,
  Mail,
  Key,
  Smartphone
} from 'lucide-react';

interface UserSettings {
  email_notifications: boolean;
  telegram_notifications: boolean;
}

interface UserProfile {
  email: string;
  created_at: string;
  telegram_id: number | null;
}

const defaultSettings: UserSettings = {
  email_notifications: true,
  telegram_notifications: false
};

const Settings = () => {
  const [settings, setSettings] = useState<UserSettings>(defaultSettings);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [changePassword, setChangePassword] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMessage, setPushMessage] = useState('');

  useEffect(() => {
    fetchUserData();
    checkPushStatus();
  }, []);

  const checkPushStatus = async () => {
    if (!isPushSupported()) return;
    try {
      const sw = await navigator.serviceWorker.ready;
      const sub = await sw.pushManager.getSubscription();
      setPushEnabled(sub !== null && Notification.permission === 'granted');
    } catch {
      setPushEnabled(false);
    }
  };

  const isIOSWithoutPWA = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as any).standalone === true;
    return isIOS && !isStandalone;
  };

  const handlePushToggle = async (enabled: boolean) => {
    setPushLoading(true);
    setPushMessage('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setPushMessage('Sesión expirada. Vuelve a iniciar sesión.');
        return;
      }
      if (enabled) {
        const ok = await subscribeToPush(session.access_token);
        setPushEnabled(ok);
        setPushMessage(ok ? 'Notificaciones push activadas' : 'Error al activar push');
      } else {
        const ok = await unsubscribeFromPush(session.access_token);
        setPushEnabled(!ok);
        setPushMessage(ok ? 'Notificaciones push desactivadas' : 'Error al desactivar push');
      }
    } catch {
      setPushMessage('Error inesperado');
    } finally {
      setPushLoading(false);
      setTimeout(() => setPushMessage(''), 3000);
    }
  };

  const fetchUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Obtener perfil
      const { data: profileData } = await supabase
        .from('profiles')
        .select('telegram_id')
        .eq('id', user.id)
        .single();

      setProfile({
        email: user.email || '',
        created_at: user.created_at,
        telegram_id: profileData?.telegram_id ?? null
      });

      // Obtener configuraciones
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code === 'PGRST116') {
        // Si no hay configuraciones, crear unas por defecto
        const { error: insertError } = await supabase
          .from('user_settings')
          .insert([{
            user_id: user.id,
            ...defaultSettings
          }]);

        if (insertError) throw insertError;
        setSettings(defaultSettings);
      } else if (error) {
        throw error;
      } else {
        setSettings(data);
      }
    } catch (err: any) {
      console.error('Error fetching user data:', err);
      setError('Error al cargar los datos del usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = (key: keyof UserSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess(false);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          ...settings
        });

      if (error) throw error;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError('Error al guardar las configuraciones');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (changePassword.new !== changePassword.confirm) {
      setError('Las contraseñas nuevas no coinciden');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess(false);

      const { error } = await supabase.auth.updateUser({
        password: changePassword.new
      });

      if (error) throw error;

      setSuccess(true);
      setChangePassword({ current: '', new: '', confirm: '' });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error updating password:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white">Configuración</h1>
          <p className="mt-2 text-gray-400">
            Administra tu perfil y preferencias
          </p>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-900/20 border border-green-900/50 rounded-lg p-4 flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
            <p className="text-green-500">Cambios guardados correctamente</p>
          </div>
        )}

        {/* Profile Section */}
        <div className="card space-y-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Perfil</h2>
              <p className="text-sm text-gray-400">Tu información de cuenta</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300">Email</label>
              <div className="flex items-center mt-1">
                <Mail className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-white">{profile?.email}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300">Miembro desde</label>
              <span className="text-white">
                {new Date(profile?.created_at || '').toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Password Section */}
        <div className="card space-y-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Key className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Cambiar Contraseña</h2>
              <p className="text-sm text-gray-400">Actualiza tu contraseña de acceso</p>
            </div>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300">Nueva Contraseña</label>
              <input
                type="password"
                value={changePassword.new}
                onChange={(e) => setChangePassword(prev => ({ ...prev, new: e.target.value }))}
                className="input-field mt-1"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300">Confirmar Nueva Contraseña</label>
              <input
                type="password"
                value={changePassword.confirm}
                onChange={(e) => setChangePassword(prev => ({ ...prev, confirm: e.target.value }))}
                className="input-field mt-1"
                required
              />
            </div>
            <div>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary"
              >
                {saving ? 'Actualizando...' : 'Actualizar Contraseña'}
              </button>
            </div>
          </form>
        </div>

        {/* Notifications */}
        <div className="card space-y-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Bell className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Notificaciones</h2>
              <p className="text-sm text-gray-400">Configura cómo quieres recibir las alertas</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-white">Notificaciones por Email</h3>
                <p className="text-sm text-gray-400">Recibe actualizaciones en tu correo</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.email_notifications}
                  onChange={(e) => handleSettingsChange('email_notifications', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-white">Notificaciones de Telegram</h3>
                <p className="text-sm text-gray-400">Recibe alertas instantáneas</p>
                {settings.telegram_notifications && !profile?.telegram_id && (
                  <p className="text-xs text-yellow-500 mt-1">
                    Vincula tu Telegram en <a href="/profile" className="underline">Mi Perfil</a> para recibir alertas
                  </p>
                )}
                {settings.telegram_notifications && profile?.telegram_id && (
                  <p className="text-xs text-green-400 mt-1">
                    Vinculado — ID {profile.telegram_id}
                  </p>
                )}
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.telegram_notifications}
                  onChange={(e) => handleSettingsChange('telegram_notifications', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            {/* Push Notifications */}
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2">
                  <Smartphone className="h-4 w-4 text-gray-400" />
                  <h3 className="text-sm font-medium text-white">Notificaciones Push</h3>
                </div>
                <p className="text-sm text-gray-400">
                  Recibe alertas en tu dispositivo sin abrir la app
                </p>
                {!isPushSupported() && (
                  <p className="text-xs text-yellow-500 mt-1">
                    Tu navegador no soporta notificaciones push
                  </p>
                )}
                {isPushSupported() && isIOSWithoutPWA() && (
                  <p className="text-xs text-yellow-500 mt-1">
                    Para recibir notificaciones en iOS, añade la app a tu pantalla de inicio primero
                  </p>
                )}
                {pushMessage && (
                  <p className="text-xs text-green-400 mt-1">{pushMessage}</p>
                )}
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={pushEnabled}
                  onChange={(e) => handlePushToggle(e.target.checked)}
                  disabled={pushLoading || !isPushSupported()}
                  className="sr-only peer"
                />
                <div className={`w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary ${!isPushSupported() ? 'opacity-50' : ''}`}></div>
              </label>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={saveSettings}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;